import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import {
  useBillingControllerCancel,
  shopifyBillingControllerConfirm,
  useBillingControllerGetFigmaPlugin,
  useBillingControllerGetSubscription,
  billingControllerUpdatePaymentMethod,
  useBillingControllerCreateSetupIntent,
} from '@/apis/backend/generated/billing/billing';

import {
  Box,
  Card,
  Chip,
  Stack,
  Alert,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogContentText,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useOrganization } from 'src/context/organization-context';
import { useSubscription } from 'src/context/subscription-context';

import { BrandPlansPanel } from 'src/components/billing/BrandPlansPanel';
import { ShopifyBillingPanel } from 'src/components/billing/ShopifyBillingPanel';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/settings/organization/billing')({
  // Merchants land here after approving a plan on Shopify's hosted pricing page.
  // shopify_billing is set by the API's /billing/shopify/return handler, which has
  // ALREADY reconciled the subscription server-side -- we only refresh and report.
  // shopify_return/plan_handle are the older browser-side confirm path, kept so a
  // welcome link still pointing at this page keeps working.
  validateSearch: (search: Record<string, unknown>) => ({
    shopify_billing: search.shopify_billing as 'updated' | 'error' | undefined,
    shopify_return: search.shopify_return as string | undefined,
    plan_handle: search.plan_handle as string | undefined,
  }),
  component: BillingSettingsPage,
});

function BillingSettingsPage() {
  const { t } = useTranslation('settings');
  const { activeOrganization } = useOrganization();
  const { requiresSubscription } = useSubscription();
  const orgId = activeOrganization?.id || '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Provider is fixed at org creation. Shopify-billed orgs never see the Stripe UI.
  const isShopify = (activeOrganization?.billingProvider as string) === 'shopify';

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Update payment method state
  const [updatePmClientSecret, setUpdatePmClientSecret] = useState<string | null>(null);

  const { data: subscription, isLoading: subLoading } = useBillingControllerGetSubscription(orgId, {
    query: { enabled: !!orgId },
  });

  const invalidateBilling = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes('/billing/');
      },
    });
  };

  // Post-approval return from the API's Shopify return handler. The plan change is
  // already persisted at this point (it has to be: the merchant may not have been
  // logged in when Shopify redirected them), so just pull the fresh state and report.
  const shopifyReturnHandled = useRef(false);
  useEffect(() => {
    if (shopifyReturnHandled.current) return;
    if (!orgId || !search.shopify_billing) return;

    shopifyReturnHandled.current = true;
    if (search.shopify_billing === 'updated') {
      invalidateBilling();
      toast.success(t('orgBilling.toast.planUpdated', 'Your plan has been updated.'));
    } else {
      toast.error(
        t(
          'orgBilling.toast.shopifyReturnError',
          'We could not read your plan from Shopify. It will sync automatically in a few minutes.',
        ),
      );
    }
    navigate({
      to: paths.dashboard.orgSettings(orgId).organization.billing,
      replace: true,
      search: { shopify_billing: undefined, shopify_return: undefined, plan_handle: undefined },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, search.shopify_billing]);

  // Legacy browser-side confirm path, for a welcome link still pointing at this page.
  const shopifyConfirmAttempted = useRef(false);
  useEffect(() => {
    if (shopifyConfirmAttempted.current) return;
    if (!orgId || !isShopify) return;
    if (!search.shopify_return && !search.plan_handle) return;

    shopifyConfirmAttempted.current = true;
    shopifyBillingControllerConfirm(orgId, { planHandle: search.plan_handle })
      .then(() => {
        toast.success(t('orgBilling.toast.subscriptionActivated', 'Subscription activated!'));
        invalidateBilling();
      })
      .catch((err: any) => {
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            t('orgBilling.toast.shopifyConfirmError', 'Could not confirm your Shopify subscription. Please try again.'),
        );
      })
      .finally(() => {
        navigate({
          to: paths.dashboard.orgSettings(orgId).organization.billing,
          replace: true,
          search: { shopify_return: undefined, plan_handle: undefined },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, isShopify, search.shopify_return, search.plan_handle]);

  const setupIntentMutation = useBillingControllerCreateSetupIntent();

  const cancelMutation = useBillingControllerCancel({
    mutation: {
      onSuccess: () => {
        toast.success(t('orgBilling.toast.cancelScheduled', 'Subscription will be canceled at the end of the billing period'));
        setCancelDialogOpen(false);
        invalidateBilling();
      },
      onError: (error: any) => {
        toast.error(error?.message || t('orgBilling.toast.cancelError', 'Failed to cancel subscription'));
      },
    },
  });

  // Add-on state drives the plugin-only billing surface, which has no base
  // subscription to hang off.
  const { data: figmaPlugin } = useBillingControllerGetFigmaPlugin(orgId, {
    query: { enabled: !!orgId },
  });
  const hasLiveAddon = !!figmaPlugin?.addonStatus &&
    ['active', 'trialing', 'past_due'].includes(figmaPlugin.addonStatus);

  // A comp never creates a Stripe customer, so there is no card to manage until
  // the org buys something real. Offering the button anyway promises an action
  // that can only fail.
  const canManagePaymentMethod =
    subscription?.billingProvider === 'stripe' || hasLiveAddon;

  const isLoading = subLoading;

  const handleUpdatePaymentMethod = () => {
    if (!orgId) return;
    setupIntentMutation.mutate(
      { orgId },
      {
        onSuccess: (data) => {
          setUpdatePmClientSecret(data.clientSecret);
        },
        onError: (error: any) => {
          toast.error(error?.message || t('orgBilling.toast.startPaymentUpdateError', 'Failed to start payment update'));
        },
      },
    );
  };

  const handleCancel = () => {
    if (!orgId) return;
    cancelMutation.mutate({ orgId });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('orgBilling.title', 'Billing')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('orgBilling.subtitle', 'Manage your subscription and billing information.')}
      </Typography>

      {requiresSubscription && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('orgBilling.requiresSubscriptionAlert', 'You need an active subscription to use nexmail. Choose a plan below to get started.')}
        </Alert>
      )}

      {/* Email usage is not an org-level number under per-brand pricing: the caps
          are per brand, so a single bar here misrepresents the limit. Usage lives
          with each brand instead. */}

      {isShopify ? (
        // Per-brand plans are a Stripe-only concept. Shopify merchants are billed
        // through Shopify's managed billing, where every brand is on the one plan
        // and the Figma plugin comes with it -- there is nothing to configure.
        <ShopifyBillingPanel orgId={orgId} subscription={subscription ?? null} />
      ) : (
        <>
      {/* Active subscription card */}
      {subscription && (
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {subscription.plan.name}
                </Typography>
                <StatusChip status={subscription.status} />
                {subscription.billingProvider === 'comped' && (
                  <Chip label={t('orgBilling.subscription.comped', 'Comped')} size="small" color="success" variant="outlined" />
                )}
                {subscription.discount && (
                  <Chip
                    label={
                      subscription.discount.percentOff != null
                        ? t('orgBilling.discount.percentOff', '{{pct}}% off', { pct: subscription.discount.percentOff })
                        : subscription.discount.amountOff != null
                          ? t('orgBilling.discount.amountOff', '${{amount}} off', { amount: (subscription.discount.amountOff / 100).toFixed(0) })
                          : t('orgBilling.discount.label', 'Discount')
                    }
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>

              {subscription.billingProvider === 'comped' ? (
                // A comped org is not charged, so quoting a monthly total next to
                // the "Comped" chip just reads as a contradiction.
                <Typography variant="body2" color="text.secondary">
                  {subscription.brandQuantity === 1
                    ? t('orgBilling.subscription.perBrandSingle', '{{count}} brand', { count: subscription.brandQuantity })
                    : t('orgBilling.subscription.perBrandPlural', '{{count}} brands', { count: subscription.brandQuantity })}
                  {' -- '}
                  {t('orgBilling.subscription.noCharge', 'no charge')}
                </Typography>
              ) : subscription.plan.features.pricingModel === 'per_brand' ? (
                <Typography variant="body2" color="text.secondary">
                  {subscription.brandQuantity === 1
                    ? t('orgBilling.subscription.perBrandSingle', '{{count}} brand', { count: subscription.brandQuantity })
                    : t('orgBilling.subscription.perBrandPlural', '{{count}} brands', { count: subscription.brandQuantity })}
                  {' × '}
                  ${(subscription.plan.priceMonthly / 100).toFixed(0)}
                  {' = '}
                  <strong>${((subscription.plan.priceMonthly * subscription.brandQuantity) / 100).toFixed(0)}/mo</strong>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  ${(subscription.plan.priceMonthly / 100).toFixed(0)}/mo
                  {' -- '}
                  {subscription.plan.features.emailsPerMonth === null
                    ? t('orgBilling.subscription.unlimitedEmails', 'Unlimited emails per month')
                    : t('orgBilling.subscription.emailsPerMonth', '{{count}} emails per month', { count: subscription.plan.features.emailsPerMonth })}
                  {' -- '}
                  {subscription.plan.features.maxClients === null
                    ? t('orgBilling.subscription.unlimitedBrands', 'Unlimited brands')
                    : subscription.plan.features.maxClients === 1
                      ? t('orgBilling.subscription.maxClientsSingle', '{{count}} brand', { count: subscription.plan.features.maxClients })
                      : t('orgBilling.subscription.maxClientsPlural', '{{count}} brands', { count: subscription.plan.features.maxClients })}
                </Typography>
              )}

              {subscription.billingProvider === 'comped' && (
                <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                  {t('orgBilling.subscription.comped', 'Comped')}
                  {subscription.maxClientsOverride != null && (
                    <> · {subscription.maxClientsOverride === 1
                      ? t('orgBilling.subscription.compedBrandsSingle', '{{count}} brand included', { count: subscription.maxClientsOverride })
                      : t('orgBilling.subscription.compedBrandsPlural', '{{count}} brands included', { count: subscription.maxClientsOverride })}</>
                  )}
                </Typography>
              )}

              {subscription.discount && (
                <Typography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                  {subscription.discount.name ?? t('orgBilling.discount.label', 'Discount')}:{' '}
                  {subscription.discount.percentOff != null
                    ? t('orgBilling.discount.percentOff', '{{pct}}% off', { pct: subscription.discount.percentOff })
                    : subscription.discount.amountOff != null
                      ? t('orgBilling.discount.amountOff', '${{amount}} off', { amount: (subscription.discount.amountOff / 100).toFixed(0) })
                      : t('orgBilling.discount.applied', 'applied')}
                  {' '}
                  {subscription.discount.duration === 'forever'
                    ? t('orgBilling.discount.durationForever', 'forever')
                    : subscription.discount.duration === 'once'
                      ? t('orgBilling.discount.durationOnce', '(this period only)')
                      : t('orgBilling.discount.durationMonths', { months: subscription.discount.durationInMonths ?? '?', defaultValue: 'for {{months}} months' })}
                  {subscription.discount.endsAt && (
                    <> · {t('orgBilling.discount.ends', 'ends {{date}}', { date: new Date(subscription.discount.endsAt).toLocaleDateString() })}</>
                  )}
                </Typography>
              )}

              {subscription.currentPeriodEnd && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subscription.cancelAtPeriodEnd
                    ? t('orgBilling.subscription.cancelsOn', 'Cancels on {{date}}', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString() })
                    : t('orgBilling.subscription.renewsOn', 'Renews on {{date}}', { date: new Date(subscription.currentPeriodEnd).toLocaleDateString() })}
                </Typography>
              )}

              {subscription.cancelAtPeriodEnd && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t('orgBilling.subscription.cancelAtPeriodEndAlert', 'Your subscription is set to cancel at the end of the current billing period.')}
                </Alert>
              )}
            </Box>

            <Stack spacing={1}>
              {canManagePaymentMethod && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleUpdatePaymentMethod}
                disabled={setupIntentMutation.isPending}
              >
                {setupIntentMutation.isPending ? t('orgBilling.subscription.loadingBtn', 'Loading...') : t('orgBilling.subscription.updatePaymentMethod', 'Update Payment Method')}
              </Button>
              )}
              {!subscription.cancelAtPeriodEnd && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  {t('orgBilling.subscription.cancelSubscription', 'Cancel Subscription')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Card>
      )}

      {/* An org can be paying us for an add-on with no base plan at all. It still
          needs somewhere to replace a card, and the subscription card above only
          renders for base plans. */}
      {!subscription && hasLiveAddon && (
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="subtitle2">
                {t('orgBilling.addonOnly.title', 'Figma plugin add-on')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('orgBilling.addonOnly.subtitle', 'Billed monthly for the brands you enable.')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleUpdatePaymentMethod}
              disabled={setupIntentMutation.isPending}
            >
              {setupIntentMutation.isPending
                ? t('orgBilling.subscription.loadingBtn', 'Loading...')
                : t('orgBilling.subscription.updatePaymentMethod', 'Update Payment Method')}
            </Button>
          </Stack>
        </Card>
      )}

      {/* Per-brand plans sit with the subscription they modify, above the plan
          picker -- below it they read as part of "Change Plan".

          Not gated on having a subscription: the add-on is sold per brand and
          stands on its own, so an org with no base plan still has to be able to
          reach it. That is the only route to a Figma-only brand, and gating it
          behind a base plan made the plugin unbuyable for exactly the customers
          who want it without the rest of NexMail. The panel hides itself when
          there is nothing to configure (no brands, or a Shopify-billed org). */}
      {orgId && (
        <BrandPlansPanel
          orgId={orgId}
          baseIsComped={subscription?.billingProvider === 'comped'}
        />
      )}

      {/* No plan picker. Under per-brand pricing the plan is not a thing an org
          holds separately from its brands -- it is what each brand is on, and
          there is exactly one of them. A picker alongside the brand plans meant
          two surfaces selling the same subscription, where the one that could
          not sell the Figma add-on was the one shown first. Choosing a brand's
          tier is the purchase now; BrandPlansPanel subscribes when the org has
          no plan behind the tier it picked. */}

      {/* Update payment method dialog */}
      <Dialog
        open={!!updatePmClientSecret}
        onClose={() => setUpdatePmClientSecret(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('orgBilling.updatePaymentDialog.title', 'Update Payment Method')}</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {updatePmClientSecret && (
            <Box sx={{ mt: 2 }}>
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: updatePmClientSecret,
                  appearance: { theme: 'stripe', variables: { borderRadius: '8px' } },
                }}
              >
                <UpdatePaymentForm
                  orgId={orgId}
                  onComplete={() => {
                    setUpdatePmClientSecret(null);
                    invalidateBilling();
                    toast.success(t('orgBilling.toast.paymentMethodUpdated', 'Payment method updated!'));
                  }}
                  onCancel={() => setUpdatePmClientSecret(null)}
                />
              </Elements>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel subscription dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>{t('orgBilling.cancelDialog.title', 'Cancel Subscription')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'orgBilling.cancelDialog.body',
              'Are you sure you want to cancel? You will retain access until the end of your current billing period.'
            )}
            {hasLiveAddon &&
              ` ${t(
                'orgBilling.cancelDialog.addonNote',
                'Your Figma plugin add-on will be cancelled at the same time.'
              )}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('orgBilling.cancelDialog.keep', 'Keep Subscription')}</Button>
          <Button
            color="error"
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? t('orgBilling.cancelDialog.canceling', 'Canceling...') : t('orgBilling.subscription.cancelSubscription', 'Cancel Subscription')}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
}

// Update payment method form
function UpdatePaymentForm({
  orgId,
  onComplete,
  onCancel,
}: {
  orgId: string;
  onComplete: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('settings');
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || t('orgBilling.toast.updatePaymentError', 'Failed to update payment method'));
        setSubmitting(false);
        return;
      }

      if (!setupIntent?.id) {
        toast.error(t('orgBilling.toast.confirmPaymentError', 'Failed to confirm payment method'));
        setSubmitting(false);
        return;
      }

      await billingControllerUpdatePaymentMethod(orgId, { setupIntentId: setupIntent.id });
      onComplete();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || t('orgBilling.toast.updatePaymentError', 'Failed to update payment method'));
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </Box>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={submitting}>
          {t('common:cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || submitting}
        >
          {submitting ? t('orgBilling.updatePaymentForm.updating', 'Updating...') : t('orgBilling.subscription.updatePaymentMethod', 'Update Payment Method')}
        </Button>
      </Stack>
    </Box>
  );
}

function StatusChip({ status }: { status: string }) {
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
    active: 'success',
    trialing: 'default',
    past_due: 'warning',
    canceled: 'error',
    unpaid: 'error',
  };
  return <Chip label={status} size="small" color={colorMap[status] || 'default'} />;
}
